/**
 * 通知ベル（業者）— 未読バッジ＋一覧。開くと既読化。通知タップで該当案件へ。
 * イベント（見積り到着/落札/チャット/工事日確定/完了/入金）は sql/19 のトリガで積まれる。
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, X } from "lucide-react";
import { api, type AppNotification } from "@/lib/api";
import { supabase } from "@/lib/supabase";

interface Props {
  onOpenCase?: (caseId: string) => void;
}

export function NotificationBell({ onOpenCase }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: unread = 0 } = useQuery({
    queryKey: ["notif-unread"],
    queryFn: () => api.unreadNotificationCount(),
    refetchInterval: 20_000,
  });
  const { data: list } = useQuery({
    queryKey: ["notif-list"],
    queryFn: () => api.listNotifications(),
    enabled: open,
  });
  const markRead = useMutation({
    mutationFn: () => api.markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notif-unread"] }),
  });

  // Realtime：新着通知が来たら即バッジ更新（RLSで本人分のみ受信）
  useEffect(() => {
    const ch = supabase
      .channel("notif")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications" }, () => {
        qc.invalidateQueries({ queryKey: ["notif-unread"] });
        qc.invalidateQueries({ queryKey: ["notif-list"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [qc]);

  useEffect(() => {
    if (open && unread > 0) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        aria-label="通知"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md hover:bg-brand-50 dark:hover:bg-brand-800/30"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[86vw] max-h-[70vh] overflow-y-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] shadow-xl z-50">
          <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg)]">
            <p className="text-sm font-bold">通知</p>
            <button onClick={() => setOpen(false)} className="p-1 text-ink-soft"><X size={16} /></button>
          </div>
          {(!list || list.length === 0) ? (
            <p className="px-4 py-8 text-sm text-ink-soft text-center">通知はありません</p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {list.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => { setOpen(false); if (n.case_id && onOpenCase) onOpenCase(n.case_id); }}
                    className={`w-full text-left px-4 py-3 hover:bg-brand-50 dark:hover:bg-brand-800/20 ${n.read ? "" : "bg-brand-50/50 dark:bg-brand-800/10"}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{n.title}</p>
                        {n.body && <p className="text-xs text-ink-soft dark:text-white/60 mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[11px] text-ink-soft dark:text-white/40 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 1) return "たった今";
  if (m < 60) return `${Math.floor(m)}分前`;
  if (m < 1440) return `${Math.floor(m / 60)}時間前`;
  return `${Math.floor(m / 1440)}日前`;
}

export type { AppNotification };
